/* eslint-disable react/forbid-prop-types */
// -----------------------------------------------------------------------------
// AttendedPopupShell — shared structure for the attended popups
// (AttendedAppointment / ConsultationAttendedAppointment).
//
// Phase 3 of the popup refactor. The shell owns the SHARED chrome and the
// flowStep-driven render gates (unified in Phase 1); each popup keeps its own
// hooks/state/derivations and passes the genuinely per-popup pieces as slots:
//   • headerNode   — the already-resolved header for the current flowStep
//                    (popup picks slim vs full / "Booked" chip layout itself).
//   • gateNode     — the attendance-gate block (AA only); rendered only when
//                    flowStep === 'ATTENDANCE_GATE'. CAA passes null.
//   • reminderNode — the useReminderContent output (inline checklist/reminder).
//   • tcoNode      — the popup's <TCOScript> (needs the popup's own ref/handlers).
//
// SHARED in the shell (identical in both popups): the "What's next?" picker (two
// mutually-exclusive row layouts), the OUTCOME box, the Cancel/Finish footer, and
// the debug panel. These are driven purely by props so the rendered DOM is
// byte-identical to what each popup produced inline before.
//
// The shell renders no business logic: outcome selection, finish gating, and
// disabled state are all computed by the popup and passed in.
// -----------------------------------------------------------------------------
import { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, ButtonBase, Typography, Container, Stack, Tooltip, Zoom, IconButton, Collapse, Skeleton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import SimilarLeadWarningPopup from '../../sections/@dashboard/leadDetail/SimilarLeadWarningPopup';
import { AnimatedSection } from '../../pages/dashboard/analyticsReports/components';
import { FormProvider } from '../hook-form';
import Iconify from '../Iconify';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { attendedFooterStyle } from '../../styles/AttendedAppointment';
import { info } from '../../theme/palette';

// Outcome chip palette — identical to the inline copies the popups used before.
const OUTCOME_META = {
    'Reminder & Follow-up': { color: '#FFAB00', bg: 'rgba(255,171,0,0.10)' },
    'Starting treatment': { color: '#00AB55', bg: 'rgba(0,171,85,0.10)' },
    'ClinCheck': { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' },
    'Treatment complete': { color: '#7B61FF', bg: 'rgba(123,97,255,0.10)' },
    'Archive': { color: '#FF5630', bg: 'rgba(255,86,48,0.10)' },
};

const AttendedPopupShell = forwardRef(({
    practiceId,
    flowStep,
    isInitiallyLoading,
    activeTab,
    onActiveTabChange,
    tabs,
    // Outcome tabs NOT shown as picker chips, but still MOUNTABLE in the OUTCOME
    // box when activeTab matches (so their ref-bearing component can be submitted).
    // 'Treatment complete' lives here — it's driven from the footer button, not
    // the picker.
    hiddenTabs,
    // splitGroup — when true, pin the FIRST chip to the LEFT and the REMAINING
    // chips to the RIGHT of the chip strip by inserting a flex-grow spacer between
    // tabs[0] and tabs.slice(1). Used by FollowUpV2 so the Active Tasks chip
    // anchors left while Book/Archive/Can't Reach anchor right. Attended doesn't
    // pass it → defaults to false → chips render in a continuous left-aligned row.
    // Added 2026-06-07.
    splitGroup,
    // chipMinWidth — fixed pixel width of each chip in the CONTENT-flowStep strip.
    // Default 62 preserves Attended's existing visual. FollowUpV2 passes a larger
    // value (140) so chip labels like 'Book appointment' fit without the ellipsis
    // truncation rule kicking in. Per-instance override → Attended unchanged.
    // Added 2026-06-07.
    chipMinWidth,
    // alwaysShowChipLabels — when true, the OUTCOME_CHOSEN-row UNSELECTED chips
    // render icon + label (instead of icon-only IconButton). Selected chip already
    // shows icon + label; this just brings the unselected siblings to parity so
    // users can read what each chip does without hovering for a tooltip.
    // FollowUpV2 passes true; Attended doesn't (default false) so its compact
    // icon-only IconButton row is preserved. Added 2026-06-07.
    alwaysShowChipLabels,
    // Footer "Treatment complete" secondary button (footer-only outcome).
    onTreatmentComplete,
    treatmentCompleteLoading,
    showTreatmentCompleteButton,
    // When the lead has >1 treatment, the name of the treatment the footer actions
    // actually target (both "Starting treatment" and "Treatment complete" operate on
    // the same resolved treatment).  Single-treatment leads pass null → no label,
    // zero visual change.  Added 2026-06-17 for multi-tx popup action clarity.
    actionTreatmentLabel,
    // slots
    showSimilarLeadWarning,
    tcoNode,
    methods,
    onFormSubmit,
    headerNode,
    gateNode,
    reminderNode,
    // OUTCOME box — optional "Back to checklist" affordance (CAA only)
    outcomeBackToChecklist,
    // footer
    onFinish,
    finishLoading,
    finishDisabled,
    finishClassName,
    // Dynamic Finish-button copy. Default 'Finish' keeps Attended's wording untouched
    // (Attended doesn't pass this prop — see feedback-review-destination-component).
    // FollowUpV2 passes per-(activeTab, bookFlowStep) values so the multi-step Book
    // wizard reads "Next" mid-flow and "Finish booking" on the last step.
    finishLabel,
    finishHelperText,
    // Optional secondary Back button rendered between Cancel and Finish. All three
    // props must be set for the Back button to render; missing any → unchanged
    // footer (Attended-safe default). Added 2026-06-07 for multi-step Book wizard.
    showBack,
    onBack,
    backLabel,
    isFinishAvailable,
    themeStretch,
    handleClose,
    // debug
    debugStateObject,
    attDebug,
    dbgSx,
    DbgTag,
}, ref) => { // eslint-disable-line no-unused-vars
    // Footer/action bar visibility — hidden when nothing actionable is available
    // (e.g. the attendance gate). Prevents an empty blurred bar showing when a
    // treatment has no TCO scripts. The TCO button (when scripts exist) shows in
    // the bar whenever Finish is available, which is the View-Tasks flow.
    const getIsVisible = () => (isFinishAvailable ? {} : { display: 'none' });
    const [debugOpen, setDebugOpen] = useState(false);

    return (
        <Box>
            {showSimilarLeadWarning && <SimilarLeadWarningPopup type={'DialogPopup'} dialogClose={() => handleClose()} />}
            <FormProvider methods={methods} onSubmit={onFormSubmit}>
                {headerNode}
                {flowStep === 'ATTENDANCE_GATE' && gateNode}
            </FormProvider>
            {/* Fixed-height skeleton — stabilises popup dimensions during the initial
                guided-checklist fetch so the dialog doesn't jump when content lands.
                Only shown on the very first load (isInitiallyLoading); post-save /
                post-pause refreshes that briefly revisit LOADING skip this so the
                user doesn't see a skeleton flash mid-action. */}
            {flowStep === 'LOADING' && isInitiallyLoading && (
                <Box sx={{ mt: 0 }}>
                    {/* Approximate "What's next?" picker card */}
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#ffffff', borderRadius: '12px', width: '100%' }}>
                        <Skeleton variant="text" width={80} height={14} sx={{ mb: 1.25 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Skeleton variant="rounded" width={62} height={56} sx={{ borderRadius: '8px' }} />
                            <Skeleton variant="rounded" width={62} height={56} sx={{ borderRadius: '8px' }} />
                            <Skeleton variant="rounded" width={62} height={56} sx={{ borderRadius: '8px' }} />
                        </Box>
                    </Box>
                    {/* Approximate checklist / reminder body */}
                    <Box sx={{ mb: 2, p: 3, bgcolor: '#ffffff', borderRadius: '12px', width: '100%' }}>
                        <Skeleton variant="text" width="55%" height={20} sx={{ mb: 1.5 }} />
                        <Skeleton variant="rounded" height={52} sx={{ mb: 1, borderRadius: '8px' }} />
                        <Skeleton variant="rounded" height={52} sx={{ mb: 1, borderRadius: '8px' }} />
                        <Skeleton variant="rounded" height={52} sx={{ borderRadius: '8px' }} />
                    </Box>
                </Box>
            )}
            {/* Compact "What's next?" picker — exactly one of two row layouts renders
                at any moment (mutually exclusive on flowStep). Always inline. */}
            {flowStep !== 'ATTENDANCE_GATE' && flowStep !== 'LOADING' && <AnimatedSection delay={0} sx={{ mt: 0, mb: 2, p: 2, bgcolor: '#ffffff', borderRadius: '12px', width: '100%', ...dbgSx('#AA00FF') }}>
                <DbgTag color="#AA00FF" label="WHATS-NEXT PICKER" />
                {flowStep === 'CONTENT' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                        <Typography sx={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#6e6e73', flexShrink: 0 }}>
                            What&apos;s next?
                        </Typography>
                        {/*
                            splitGroup (default false) — when true, pin the FIRST chip to the
                            LEFT and the remaining chips to the RIGHT of the same row by inserting
                            a flex-grow spacer between tabs[0] and tabs.slice(1). FollowUpV2 sets
                            this so 'Active tasks' anchors left while Book/Archive/Can't Reach
                            anchor right. Attended doesn't pass the prop → all chips render in
                            a continuous left-aligned row (unchanged behavior). Added 2026-06-07.

                            Render strategy: same .map over `tabs` either way, but when splitGroup
                            is true we splice a spacer Box at index 1. Keeps the chip JSX identical
                            so styling/handlers can't drift between the two paths.
                        */}
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flex: 1, minWidth: 0, flexWrap: 'wrap', rowGap: 0.75 }}>
                            {tabs.flatMap((tab, idx) => {
                                // `tab.value` is the internal identifier (used by shell gates
                                // at lines 195/203 + by onActiveTabChange / OUTCOME_META keys).
                                // `tab.label` is the user-facing chip text — added 2026-06-07 to
                                // allow callers (e.g. FollowUpV2) to rename a chip without breaking
                                // the value-coupled shell logic. Backward compatible: when label
                                // is undefined, fall back to value.
                                const m = OUTCOME_META[tab.value] || { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' };
                                const displayLabel = tab.label || tab.value;
                                // 2026-06-07 bug-fix #1 + #2:
                                //   #1 — convert chip from <Box> → <ButtonBase> so keyboard users
                                //        get tab focus + Enter/Space activation + focus-visible
                                //        ring + correct ARIA role for free. ButtonBase renders a
                                //        <button> by default; we keep all visual styling intact.
                                //   #2 — when alwaysShowChipLabels is true (V2 path), bump icon
                                //        to 20px and label to 12px/500 so icon and label have
                                //        comparable visual weight. Attended doesn't pass this
                                //        prop → keeps the existing 18px icon + 10px label, so
                                //        Attended chip sizing is byte-identical to before.
                                const iconPx = alwaysShowChipLabels ? 20 : 18;
                                const labelFontSize = alwaysShowChipLabels ? '12px' : '10px';
                                const labelFontWeight = alwaysShowChipLabels ? 500 : 400;
                                const chipNode = (
                                    <Tooltip key={tab.value} title={displayLabel} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 250 }}>
                                        <ButtonBase
                                            component="button"
                                            type="button"
                                            aria-label={displayLabel}
                                            aria-pressed={activeTab === tab.value}
                                            onClick={(e) => { e.stopPropagation(); onActiveTabChange(tab.value); }}
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.25,
                                                // chipMinWidth default 62px preserves Attended's existing
                                                // chip dimensions. V2 passes a larger value (140) so chip
                                                // labels like "Book appointment" / "Active tasks" don't
                                                // truncate with the ellipsis rules below.
                                                width: chipMinWidth,
                                                py: 0.5,
                                                px: 0.75,
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                bgcolor: m.bg,
                                                border: '1px solid transparent',
                                                userSelect: 'none',
                                                transition: 'border-color 200ms cubic-bezier(0.32, 0.72, 0, 1), transform 280ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                '&:hover': { borderColor: m.color, bgcolor: `${m.color}26` },
                                                '&:active': { transform: 'scale(0.94)' },
                                                '&:focus-visible': { outline: `2px solid ${m.color}`, outlineOffset: '2px' },
                                            }}
                                        >
                                            <Iconify icon={tab.icon.props.icon} width={iconPx} height={iconPx} sx={{ color: m.color }} />
                                            <Typography sx={{ fontSize: labelFontSize, fontWeight: labelFontWeight, color: '#6e6e73', lineHeight: 1.2, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                                                {displayLabel}
                                            </Typography>
                                        </ButtonBase>
                                    </Tooltip>
                                );
                                // splitGroup spacer — inserted ONCE after the first chip so the
                                // remaining chips are pushed to the right edge of the Stack via
                                // flex-grow. flatMap flattens the [chip, spacer] tuple into the
                                // single child array Stack expects.
                                if (splitGroup && idx === 0 && tabs.length > 1) {
                                    return [
                                        chipNode,
                                        <Box key="__splitGroup_spacer__" sx={{ flex: 1, minWidth: 0 }} />,
                                    ];
                                }
                                return [chipNode];
                            })}
                        </Stack>
                    </Box>
                )}
                {flowStep === 'OUTCOME_CHOSEN' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                        {(() => {
                            const selectedTab = tabs.find(t => t.value === activeTab);
                            if (!selectedTab) return null;
                            const selectedMeta = OUTCOME_META[selectedTab.value] || { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' };
                            const unselectedTabs = tabs.filter(t => t.value !== activeTab);
                            return (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '8px',
                                            bgcolor: selectedMeta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            flexShrink: 0, border: `1.5px solid ${selectedMeta.color}`,
                                            transition: 'all 0.35s ease',
                                        }}>
                                            <Iconify icon={selectedTab.icon.props.icon} width={16} height={16} sx={{ color: selectedMeta.color }} />
                                        </Box>
                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em', lineHeight: 1.3 }}>
                                            {selectedTab.label || selectedTab.value}
                                        </Typography>
                                    </Box>
                                    <Stack direction="row" spacing={0.75} alignItems="center">
                                        {unselectedTabs.map((tab) => {
                                            const m = OUTCOME_META[tab.value] || { color: '#078DEE', bg: 'rgba(7,141,238,0.10)' };
                                            const displayLabel = tab.label || tab.value;
                                            // When alwaysShowChipLabels is true, render icon + muted label
                                            // (still smaller / less prominent than the selected chip) so
                                            // users can read each chip's purpose without hovering. Default
                                            // false → falls back to the original icon-only IconButton row
                                            // Attended has always used.
                                            if (alwaysShowChipLabels) {
                                                // 2026-06-07 bug-fix #1: ButtonBase for keyboard a11y.
                                                // Visual styling unchanged (height 32, px 1.25, etc.).
                                                return (
                                                    <Tooltip key={tab.value} title={displayLabel} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 250 }}>
                                                        <ButtonBase
                                                            component="button"
                                                            type="button"
                                                            aria-label={displayLabel}
                                                            aria-pressed={false}
                                                            onClick={() => onActiveTabChange(tab.value)}
                                                            sx={{
                                                                display: 'flex', alignItems: 'center', gap: 0.75,
                                                                height: 32, px: 1.25, borderRadius: '8px',
                                                                bgcolor: m.bg, border: '1px solid transparent',
                                                                cursor: 'pointer', userSelect: 'none',
                                                                transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), background-color 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                                '&:hover': { borderColor: m.color, bgcolor: `${m.color}26` },
                                                                '&:active': { transform: 'scale(0.94)' },
                                                                '&:focus-visible': { outline: `2px solid ${m.color}`, outlineOffset: '2px' },
                                                            }}
                                                        >
                                                            <Iconify icon={tab.icon.props.icon} width={15} height={15} sx={{ color: m.color, flexShrink: 0 }} />
                                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#6e6e73', letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>
                                                                {displayLabel}
                                                            </Typography>
                                                        </ButtonBase>
                                                    </Tooltip>
                                                );
                                            }
                                            return (
                                                <Tooltip key={tab.value} title={displayLabel} placement="bottom" TransitionComponent={Zoom} TransitionProps={{ timeout: 250 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onActiveTabChange(tab.value)}
                                                        sx={{
                                                            width: 32, height: 32, borderRadius: '8px',
                                                            bgcolor: m.bg, border: '1px solid transparent',
                                                            transition: 'border-color 250ms cubic-bezier(0.32, 0.72, 0, 1), background-color 250ms cubic-bezier(0.32, 0.72, 0, 1), transform 350ms cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                            '&:hover': { borderColor: m.color, bgcolor: `${m.color}26` },
                                                            '&:active': { transform: 'scale(0.92)' },
                                                        }}
                                                    >
                                                        <Iconify icon={tab.icon.props.icon} width={15} height={15} sx={{ color: m.color }} />
                                                    </IconButton>
                                                </Tooltip>
                                            );
                                        })}
                                    </Stack>
                                </>
                            );
                        })()}
                    </Box>
                )}
            </AnimatedSection>}
            {/* Inline checklist + reminder block — sits BELOW the picker. Unified
                flowStep rule: show in CONTENT, or in OUTCOME_CHOSEN when the chosen
                outcome is Reminder & Follow-up (the R&F content lives inline, NOT in
                the OUTCOME box — see the OUTCOME suppression rule below). */}
            {hasFeatureAccess('tasks-v2', practiceId) && (flowStep === 'CONTENT' || (flowStep === 'OUTCOME_CHOSEN' && activeTab === 'Reminder & Follow-up')) && (
                <Box className="gchk-inline-popup-wrap" sx={{ mb: 2, width: '100%' }}>
                    {reminderNode}
                </Box>
            )}
            {/* OUTCOME box — render the chosen outcome's component, EXCEPT Reminder &
                Follow-up on a tasks-v2 practice (that renders inline above; suppress here
                to avoid a duplicate ScheduleTaskReminder). */}
            {flowStep === 'OUTCOME_CHOSEN' && !(hasFeatureAccess('tasks-v2', practiceId) && activeTab === 'Reminder & Follow-up') && (
                <Box sx={{ mb: 2, p: 3, bgcolor: '#ffffff', borderRadius: '12px', width: '100%', ...dbgSx('#D50000') }}>
                    <DbgTag color="#D50000" label="OUTCOME" />
                    {outcomeBackToChecklist}
                    {/* Include hiddenTabs so the footer-driven 'Treatment complete'
                        outcome still mounts its ref-bearing component here when its
                        activeTab is selected. */}
                    {[...tabs, ...(hiddenTabs || [])].find(t => t.value === activeTab)?.component}
                </Box>
            )}
            <Box
                sx={[attendedFooterStyle, getIsVisible(), (attDebug ? { outline: '2px dashed #00B8D4', outlineOffset: '-2px' } : null), {
                    pt: 1.5,
                    bgcolor: 'rgba(246, 246, 248, 0.82)',
                    backdropFilter: 'saturate(180%) blur(20px)',
                    WebkitBackdropFilter: 'saturate(180%) blur(20px)',
                    borderTop: '0.5px solid rgba(0, 0, 0, 0.10)',
                    // Safe-area inset keeps the action bar above the iOS home
                    // indicator. env() resolves to 0 on devices without safe
                    // areas so this is a no-op on desktop / Android.
                    paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
                }]}
            >
                <DbgTag color="#00B8D4" label="ACTION BAR" />
                <Container
                    maxWidth={themeStretch ? false : 'xl'}
                    sx={{ mt: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}
                >
                    {/* Open TCO Scripts lives in the action bar now (moved from the top).
                        The redundant footer Cancel was removed — the header keeps a Cancel. */}
                    {tcoNode}
                    {/* Treatment complete — footer-only secondary outcome. Outlined,
                        less prominent than Finish, in the 'Treatment complete'
                        OUTCOME_META colour (#7B61FF). Gated on the same availability
                        as Finish (showTreatmentCompleteButton already folds in
                        isFinishAvailable + flowStep !== ATTENDANCE_GATE). */}
                    {showTreatmentCompleteButton && (
                        <LoadingButton
                            data-testid="treatment-complete-btn"
                            variant='outlined'
                            loading={treatmentCompleteLoading}
                            loadingPosition='start'
                            onClick={onTreatmentComplete}
                            disabled={finishDisabled || treatmentCompleteLoading}
                            sx={{
                                color: '#7B61FF',
                                borderColor: '#7B61FF66',
                                fontSize: '13px',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                borderRadius: '6px',
                                boxShadow: 'none',
                                px: 2.25,
                                py: 0.75,
                                minWidth: '88px',
                                '&:hover': { borderColor: '#7B61FF', bgcolor: 'rgba(123,97,255,0.06)' },
                                '&:active': { bgcolor: 'rgba(123,97,255,0.12)' },
                                '&.Mui-disabled': { borderColor: 'rgba(123,97,255,0.25)', color: 'rgba(123,97,255,0.45)' },
                            }}
                        >
                            Treatment complete
                            {/* Multi-treatment qualifier — only shown when the lead has >1
                                treatment so reps know which treatment this footer action targets.
                                Uses the same info-palette chip pattern as ChecklistListV2 so the
                                treatment-name visual language is consistent across the popup. */}
                            {actionTreatmentLabel && (
                                <Box
                                    component="span"
                                    data-testid="treatment-complete-action-label"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        ml: 0.75,
                                        px: 0.75,
                                        py: 0.125,
                                        borderRadius: '980px',
                                        bgcolor: alpha(info.main, 0.12),
                                        color: info.dark,
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        letterSpacing: '-0.005em',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 100,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        verticalAlign: 'middle',
                                    }}
                                >
                                    {actionTreatmentLabel}
                                </Box>
                            )}
                        </LoadingButton>
                    )}
                    {/* Back button — secondary outline, rendered ONLY when caller wires
                        showBack + onBack (and optionally backLabel). Attended doesn't
                        pass these → button never renders, footer unchanged. Sits between
                        Cancel and Finish so the natural read order is Cancel / Back /
                        Action. Same height + 13px/600 typography family as the other
                        footer buttons. */}
                    {showBack && onBack && (
                        <Button
                            data-testid="attended-popup-shell-back-btn"
                            variant='outlined'
                            onClick={onBack}
                            sx={{
                                color: '#1d1d1f',
                                borderColor: 'rgba(0,0,0,0.20)',
                                fontSize: '13px',
                                fontWeight: 600,
                                letterSpacing: '-0.01em',
                                textTransform: 'none',
                                borderRadius: '6px',
                                boxShadow: 'none',
                                px: 2.25,
                                py: 0.75,
                                minWidth: '88px',
                                '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: 'rgba(0,0,0,0.04)' },
                                '&:active': { bgcolor: 'rgba(0,0,0,0.08)' },
                            }}
                        >
                            {backLabel}
                        </Button>
                    )}
                    {finishHelperText && (
                        <Typography
                            data-testid="attended-popup-shell-finish-helper"
                            sx={{
                                fontSize: '12px',
                                fontWeight: 500,
                                color: '#6e6e73',
                                letterSpacing: 0,
                                lineHeight: 1.3,
                                maxWidth: 220,
                                textAlign: 'right',
                            }}
                        >
                            {finishHelperText}
                        </Typography>
                    )}
                    <LoadingButton
                        loading={finishLoading}
                        loadingPosition='start'
                        onClick={onFinish}
                        className={finishClassName}
                        disabled={finishDisabled}
                        sx={{
                            bgcolor: '#00AB55',
                            color: '#ffffff',
                            fontSize: '13px',
                            fontWeight: 600,
                            letterSpacing: '-0.01em',
                            textTransform: 'none',
                            borderRadius: '6px',
                            boxShadow: 'none',
                            px: 2.25,
                            py: 0.75,
                            minWidth: '88px',
                            '&:hover': { bgcolor: '#02934A', boxShadow: 'none' },
                            '&:active': { bgcolor: '#017A3D' },
                            '&.Mui-disabled': { bgcolor: 'rgba(0, 171, 85, 0.35)', color: '#ffffff' },
                            // MUI v5 positions the start indicator absolutely at
                            // left:14px and only reserves room for it when a
                            // startIcon is present. There is no icon here, so the
                            // spinner rendered flush against the label with no gap.
                            // Reserve the space ourselves, and ONLY while loading,
                            // so the idle button keeps its current width.
                            '&.MuiLoadingButton-loading': { pl: 4.75 },
                            '& .MuiLoadingButton-loadingIndicatorStart': { left: 14 },
                        }}
                    >
                        {finishLabel}
                    </LoadingButton>
                </Container>
            </Box>
            {attDebug && debugStateObject && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 8,
                        left: 8,
                        zIndex: 99999,
                        bgcolor: 'rgba(0,0,0,0.85)',
                        color: '#fff',
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        lineHeight: 1.5,
                        maxWidth: 360,
                        borderRadius: '8px',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        onClick={() => setDebugOpen((prev) => !prev)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: '10px',
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <Box component="span" sx={{ fontWeight: 700 }}>🐞 ATTENDED DEBUG</Box>
                        <Iconify
                            icon={debugOpen ? 'eva:chevron-up-fill' : 'eva:chevron-down-fill'}
                            width={16}
                            height={16}
                            sx={{ color: '#fff', ml: 1, flexShrink: 0 }}
                        />
                    </Box>
                    <Collapse in={debugOpen}>
                        <Box sx={{ maxHeight: '55vh', overflow: 'auto', px: '10px', pb: '10px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                <Box
                                    component="button"
                                    type="button"
                                    onClick={() => navigator.clipboard.writeText(JSON.stringify(debugStateObject, null, 2))}
                                    sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '11px',
                                        color: '#fff',
                                        bgcolor: 'rgba(255,255,255,0.15)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        borderRadius: '4px',
                                        px: 0.75,
                                        py: 0.25,
                                        cursor: 'pointer',
                                    }}
                                >
                                    Copy
                                </Box>
                            </Box>
                            {Object.entries(debugStateObject).map(([k, v]) => (
                                <Box key={k} sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                    <Box component="span" sx={{ color: '#9cdcfe' }}>{k}</Box>
                                    {': '}
                                    {typeof v === 'string' ? v : JSON.stringify(v)}
                                </Box>
                            ))}
                        </Box>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
});

AttendedPopupShell.propTypes = {
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    flowStep: PropTypes.oneOf(['LOADING', 'ATTENDANCE_GATE', 'CONTENT', 'OUTCOME_CHOSEN']).isRequired,
    activeTab: PropTypes.string,
    onActiveTabChange: PropTypes.func.isRequired,
    tabs: PropTypes.array.isRequired,
    hiddenTabs: PropTypes.array,
    splitGroup: PropTypes.bool,
    chipMinWidth: PropTypes.number,
    alwaysShowChipLabels: PropTypes.bool,
    onTreatmentComplete: PropTypes.func,
    treatmentCompleteLoading: PropTypes.bool,
    showTreatmentCompleteButton: PropTypes.bool,
    actionTreatmentLabel: PropTypes.string,
    showSimilarLeadWarning: PropTypes.bool,
    tcoNode: PropTypes.node,
    methods: PropTypes.object.isRequired,
    onFormSubmit: PropTypes.func,
    headerNode: PropTypes.node,
    gateNode: PropTypes.node,
    reminderNode: PropTypes.node,
    outcomeBackToChecklist: PropTypes.node,
    onFinish: PropTypes.func,
    finishLoading: PropTypes.bool,
    finishDisabled: PropTypes.bool,
    finishClassName: PropTypes.string,
    /** Optional copy for the Finish button. Default 'Finish' (Attended-safe). */
    finishLabel: PropTypes.string,
    /** Optional helper copy rendered next to the footer primary action. */
    finishHelperText: PropTypes.string,
    /** Multi-step wizards: render a secondary Back button before Finish when true.
     *  All three (showBack, onBack, backLabel) must be set for the button to render. */
    showBack: PropTypes.bool,
    onBack: PropTypes.func,
    backLabel: PropTypes.string,
    isFinishAvailable: PropTypes.bool,
    /** True only during the very first guided-checklist fetch. The shell renders a
     *  fixed-height skeleton so the dialog doesn't resize when content arrives.
     *  Post-save/pause refreshes that briefly revisit LOADING should pass false so
     *  the skeleton never re-flashes mid-action. */
    isInitiallyLoading: PropTypes.bool,
    themeStretch: PropTypes.bool,
    handleClose: PropTypes.func,
    debugStateObject: PropTypes.object,
    attDebug: PropTypes.bool,
    dbgSx: PropTypes.func.isRequired,
    DbgTag: PropTypes.elementType.isRequired,
};

AttendedPopupShell.defaultProps = {
    practiceId: null,
    activeTab: '',
    hiddenTabs: [],
    splitGroup: false,
    chipMinWidth: 62,
    alwaysShowChipLabels: false,
    onTreatmentComplete: undefined,
    treatmentCompleteLoading: false,
    showTreatmentCompleteButton: false,
    showSimilarLeadWarning: false,
    tcoNode: null,
    onFormSubmit: undefined,
    headerNode: null,
    gateNode: null,
    reminderNode: null,
    outcomeBackToChecklist: null,
    onFinish: undefined,
    finishLoading: false,
    finishDisabled: false,
    finishClassName: undefined,
    // Attended-safe defaults — passing neither finishLabel nor showBack keeps the
    // existing chrome (button reads "Finish", no Back button).
    finishLabel: 'Finish',
    finishHelperText: '',
    showBack: false,
    onBack: undefined,
    backLabel: 'Back',
    isFinishAvailable: false,
    isInitiallyLoading: false,
    themeStretch: false,
    handleClose: undefined,
    debugStateObject: null,
    attDebug: false,
};

export default AttendedPopupShell;
